import { clientInstance } from '../utils/http-client'

/**
 * Generic download status response from the unified downloads endpoint.
 * Uses `item_count` instead of source-specific counts (variant_count, document_count).
 */
export interface DownloadStatusResponse {
  request_id: number
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  item_count: number
  download_url?: string
  detail?: string
  expires_at?: string
}

/**
 * Get the status of a download request from the generic status endpoint.
 *
 * @param requestId - The download request ID
 * @returns DownloadStatusResponse with status and download URL if completed
 */
export async function getDownloadStatus(
  requestId: number
): Promise<DownloadStatusResponse> {
  const response = await clientInstance.get<DownloadStatusResponse>(
    `/downloads/${requestId}/status/`
  )
  return response.data
}

/**
 * Request body for starting a V2 deals export.
 */
export interface DealsExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Response from starting a V2 deals export.
 */
export interface DealsExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 deals export via the reports endpoint.
 * Used by DealsTableComponentV2 (deal reports table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startDealsReportExportV2(
  data: DealsExportV2Request
): Promise<DealsExportV2Response> {
  const response = await clientInstance.post<DealsExportV2Response>(
    '/reports/deals/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 deals export via the contacts endpoint.
 * Used by DealsTableComponent (deals table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startDealsExportV2(
  data: DealsExportV2Request
): Promise<DealsExportV2Response> {
  const response = await clientInstance.post<DealsExportV2Response>(
    '/contacts/deals/export/v2/',
    data
  )
  return response.data
}

// ─── Contacts Export V2 (async pattern via download widget) ──────────────────

/**
 * Request body for starting a V2 contacts export.
 */
export interface ContactsExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Response from starting a V2 contacts export.
 */
export interface ContactsExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 contacts export.
 * Used by ContactsTable (contacts table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startContactsExportV2(
  data: ContactsExportV2Request
): Promise<ContactsExportV2Response> {
  const response = await clientInstance.post<ContactsExportV2Response>(
    '/contacts/export/v2/',
    data
  )
  return response.data
}

// ─── Campaigns Export V2 (async pattern via download widget) ─────────────────

/**
 * Request body for starting a V2 campaigns export.
 */
export interface CampaignsExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Response from starting a V2 campaigns export.
 */
export interface CampaignsExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 campaigns export.
 * Used by CampaignsTable (campaigns table).
 *
 * @param data - Export configuration (columns, filters, file type)
 * @returns Response with request_id for status polling
 */
export async function startCampaignsExportV2(
  data: CampaignsExportV2Request
): Promise<CampaignsExportV2Response> {
  const response = await clientInstance.post<CampaignsExportV2Response>(
    '/contacts/campaigns/export/v2/',
    data
  )
  return response.data
}

// ─── Target Table Exports V2 (async pattern via download widget) ─────────────

/**
 * Generic request body for starting a V2 target table export.
 */
export interface TargetExportV2Request {
  selected_columns: string[]
  export_type: string
  filter_params: Record<string, unknown>
  sort?: string
}

/**
 * Generic response from starting a V2 target table export.
 */
export interface TargetExportV2Response {
  request_id: number
  status: string
  progress_percentage: number
  item_count: number
}

/**
 * Start an async V2 activity export.
 */
export async function startActivityExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/activity/v2/team/export/v3/',
    data
  )
  return response.data
}

/**
 * Start an async V2 hospitals export.
 */
export async function startHospitalsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/hospital/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 K12 districts export.
 */
export async function startK12DistrictsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/k12/districts/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 universities export.
 */
export async function startUniversitiesExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/universities/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 nursing homes export.
 */
export async function startNursingHomesExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/nursing_home/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 door proxies (unmatched doors) export.
 * Supports standalone and nested (chain_proxy_id) URL patterns.
 */
export async function startDoorProxiesExportV2(
  data: TargetExportV2Request,
  chainProxyId?: number
): Promise<TargetExportV2Response> {
  const url = chainProxyId
    ? `/contacts/contactcompanies/${chainProxyId}/doors/export/v2/`
    : '/contacts/doorproxies/export/v2/'
  const response = await clientInstance.post<TargetExportV2Response>(url, data)
  return response.data
}

/**
 * Start an async V2 contact companies (unmatched chains) export.
 */
export async function startContactCompaniesExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/contacts/contactcompanies/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 restaurant places (doors) export.
 */
export async function startPlacesExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/restaurants/places/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 restaurant chains export.
 */
export async function startChainsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/restaurants/chains/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 sample requests export.
 */
export async function startSampleRequestsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/samples/sample-requests/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 chain CRM reports export.
 */
export async function startChainReportsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/reports/chains/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 door CRM reports export.
 */
export async function startDoorReportsExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/reports/doors/export/v2/',
    data
  )
  return response.data
}

/**
 * Start an async V2 deals pipeline export (reports).
 */
export async function startDealsPipelineReportExportV2(
  data: TargetExportV2Request
): Promise<TargetExportV2Response> {
  const response = await clientInstance.post<TargetExportV2Response>(
    '/reports/dashboard/deals-pipeline/export/v2/',
    data
  )
  return response.data
}

// ─── Rebate Export Functions (sync blob pattern via download widget) ──────────

/**
 * Common request body for rebate table exports.
 */
export interface RebateExportRequest {
  selected_columns: string[]
  export_type: string
}

/**
 * Export rebate enrollments for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportRebateEnrollments(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/enrollments/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Rebate Enrollments - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export attribution codes for a given offer.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportAttributionCodes(
  offerId: number,
  data: RebateExportRequest,
  filterParams: Record<string, unknown>,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/offers/${offerId}/attribution-codes/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { params: filterParams, signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Attribution Codes - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}

/**
 * Export manufacturer invoices.
 * Returns a blob for immediate download via the download widget.
 */
export async function exportManufacturerInvoices(
  data: RebateExportRequest,
  signal?: AbortSignal
): Promise<{ blob: Blob; filename: string }> {
  const response = await clientInstance.post(
    `/tradespend/firstbite/invoices/export/`,
    {
      selected_columns: data.selected_columns,
      export_type: data.export_type,
    },
    { signal, responseType: 'blob' }
  )
  return {
    blob: response.data,
    filename: `First Bite Export - Invoices - ${new Date().toLocaleDateString()}.${data.export_type}`,
  }
}
